import {
	applyHeadlineToUnitPrice,
	formatCurrency,
} from '@supply-mover-2-0/core';
import type {
	ItemPromoHeadline,
	PortalStorefrontItemCardDto,
} from '@supply-mover-2-0/shared';
import type { JSX, ReactNode } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { ImagePlaceholder } from '../ImagePlaceholder';
import { ResilientImage } from '../ResilientImage';

/**
 * The storefront's item card: image, discount pill, title, manufacturer and
 * promo-aware price. Shared so the storefront and the CRM's page builder
 * render the same card (PD-16588).
 *
 * Everything that only exists inside the storefront app is passed in rather
 * than imported: the add-to-cart control arrives as `actions`, and whether
 * this visitor may see prices arrives as `showPrice` (the storefront reads it
 * from its display settings; the builder canvas shows prices).
 */
export function StorefrontItemCard({
	item,
	onNavigate,
	onLikelyStaleUrl,
	actions,
	showPrice,
}: {
	item: PortalStorefrontItemCardDto;
	onNavigate?: () => void;
	onLikelyStaleUrl?: () => void;
	/** Floating control over the image's bottom-right corner (the cart). */
	actions?: ReactNode;
	showPrice: boolean;
}): JSX.Element {
	const { t } = useTranslation();
	return (
		<div
			role="button"
			tabIndex={0}
			onClick={onNavigate}
			onKeyDown={(e) => {
				if (onNavigate != null && (e.key === 'Enter' || e.key === ' ')) {
					e.preventDefault();
					onNavigate();
				}
			}}
			className="w-full text-left bg-brand-surface border border-brand-border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-portal-accent"
		>
			<div className="relative aspect-square bg-brand-input flex items-center justify-center">
				<ResilientImage
					src={item.primaryImageUrl}
					alt={item.description}
					className="w-full h-full object-contain"
					loading="lazy"
					onLikelyStaleUrl={onLikelyStaleUrl}
					fallback={<ImagePlaceholder />}
				/>
				{item.promoHeadline != null &&
					item.promoHeadline.displayBadgeInLists && (
						<span className="absolute top-1 left-1 px-1.5 py-0.5 rounded-md text-[10px] sm:text-xs font-bold shadow-sm bg-warning-badge text-warning-tint-fg border border-warning-tint">
							{t(item.promoHeadline.badgeText)}
						</span>
					)}
				{actions != null && (
					<div className="absolute bottom-1 right-1 z-10">{actions}</div>
				)}
			</div>
			<div className="p-2 space-y-1">
				<p className="text-xs font-medium text-default line-clamp-2 min-h-[2.25rem]">
					{t(item.description)}
				</p>
				{item.manufacturer != null && (
					<p className="text-[11px] text-brand-muted truncate">
						{t(item.manufacturer)}
					</p>
				)}
				{showPrice && (
					<StorefrontItemPrice
						price={item.price}
						contractPrice={item.contractPrice}
						promoHeadline={item.promoHeadline}
					/>
				)}
			</div>
		</div>
	);
}

/**
 * Promo-aware price block for the storefront item card. Renders nothing when
 * the price is unknown so the price slot collapses cleanly. Callers decide
 * whether this visitor may see prices at all.
 */
export function StorefrontItemPrice({
	price,
	contractPrice,
	promoHeadline,
}: {
	price: string | null;
	contractPrice: string | null;
	promoHeadline: ItemPromoHeadline | null;
}): JSX.Element {
	const { t } = useTranslation();
	const listNum = price != null ? parseFloat(price) : NaN;
	const contractNum = contractPrice != null ? parseFloat(contractPrice) : NaN;
	// The contract price wins over the list price, as it always has here.
	const basePrice = Number.isFinite(contractNum)
		? contractNum
		: Number.isFinite(listNum)
			? listNum
			: NaN;
	if (!Number.isFinite(basePrice)) {
		return <></>;
	}
	const promoFinal =
		promoHeadline != null
			? applyHeadlineToUnitPrice(basePrice, promoHeadline)
			: null;
	const promoActive = promoFinal != null && promoFinal < basePrice;

	if (promoActive) {
		return (
			<div className="flex items-center gap-1 flex-wrap">
				<span className="inline-flex items-baseline px-1.5 py-0.5 rounded-md text-default text-xs font-bold bg-warning-badge text-warning-tint-fg border border-warning-tint">
					{t(formatCurrency(promoFinal))}
				</span>
				<span className="text-[10px] text-faint line-through">
					{t(formatCurrency(basePrice))}
				</span>
			</div>
		);
	}

	return (
		<p className="text-sm font-semibold text-default">
			{t(formatCurrency(basePrice))}
		</p>
	);
}
